import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonGroup } from './ButtonGroup';
import { Button } from './Button';
import { NodeDragGhost } from './NodeDragGhost';
import { ZoneDragGhost } from './ZoneDragGhost';
import { DescriptionPanelDragGhost } from './DescriptionPanelDragGhost';
import { CommentDragGhost } from './CommentDragGhost';
import { NodeColorPalette } from './NodeColorPalette/NodeColorPalette';
import { ZoneColorPalette } from './ZoneColorPalette/ZoneColorPalette';
import { DarkTooltip } from '@framework/ui/atoms/Tooltip';
import { classNames } from '@framework/utils';
import { faComment, faFileAlt, faNoteSticky, faWindowMaximize, faSquare } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useViewMenuHandlers } from '../../hooks';
import type { ViewCollectionContents } from '@view-model/domain/view/ViewCollectionContents';
import type { ModelCollection } from '@view-model/domain/model/ModelCollection';
import type { ModelId, ViewId } from 'balus-common';
import type { StickyModelContentsOperation } from '@view-model/adapter';
import type { Point } from '@view-model/models/common/basic';
import type { ThemeColor } from '@view-model/models/common/color';
import { isEqual } from 'lodash-es';

// NOTE: この型を変更した場合には、メモ化のために propsAreEqual関数も変更する必要あり
type Props = {
  readonly: boolean;
  selectedViewIds: Set<ViewId>;
  viewContents: ViewCollectionContents;
  models: ModelCollection;
  operations: Record<ModelId, StickyModelContentsOperation>;
  handleCreatingCommentAdd: (modelId: ModelId, position: Point) => void;
  hasCreatingComment: boolean;
  onAddViewClicked: () => void;
  onSelectView: (viewId: ViewId) => void;
};

// ドラッグとクリックを区別するための移動距離閾値（px）
const DESCRIPTION_PANEL_DRAG_THRESHOLD = 5;
const COMMENT_DRAG_THRESHOLD = 5;

const propsAreEqual = (prev: Props, next: Props) => {
  return (
    prev.readonly === next.readonly &&
    isEqual(prev.selectedViewIds, next.selectedViewIds) &&
    prev.viewContents === next.viewContents &&
    prev.models === next.models &&
    prev.operations === next.operations &&
    prev.handleCreatingCommentAdd === next.handleCreatingCommentAdd &&
    prev.hasCreatingComment === next.hasCreatingComment &&
    prev.onAddViewClicked === next.onAddViewClicked &&
    prev.onSelectView === next.onSelectView
  );
};

const ViewModelToolbarFn: React.FC<Props> = ({
  readonly,
  selectedViewIds,
  viewContents,
  models,
  operations,
  handleCreatingCommentAdd,
  hasCreatingComment,
  onAddViewClicked,
  onSelectView,
}: Props) => {
  const { t } = useTranslation();
  const isSingleViewSelected = useMemo(() => !!(selectedViewIds.size === 1), [selectedViewIds]);
  const {
    handleAddDescriptionPanel,
    handleAddDescriptionPanelByDrag,
    handleAddComment,
    handleAddCommentByDrag,
    handleAddNodeWithColor,
    handleAddNodeByDragWithColor,
    handleAddZoneWithColor,
    handleAddZoneByDragWithColor,
  } = useViewMenuHandlers(
    readonly,
    selectedViewIds,
    viewContents,
    models,
    operations,
    handleCreatingCommentAdd,
    onSelectView
  );

  const [isNodePaletteOpen, setIsNodePaletteOpen] = useState(false);
  const [nodeDragPosition, setNodeDragPosition] = useState<{ x: number; y: number } | null>(null);
  const [nodeDragColor, setNodeDragColor] = useState<ThemeColor | undefined>(undefined);
  // ボタンとパレットを囲むコンテナ。コンテナ外クリックでパレットを閉じるために使用する
  const nodePaletteContainerRef = useRef<HTMLDivElement>(null);
  const [commentDragPosition, setCommentDragPosition] = useState<{ x: number; y: number } | null>(null);
  const isDraggingCommentRef = useRef(false);
  // handleMouseUp 内から hasCreatingComment の最新値を参照するための ref
  const hasCreatingCommentRef = useRef(hasCreatingComment);
  hasCreatingCommentRef.current = hasCreatingComment;

  const [isZonePaletteOpen, setIsZonePaletteOpen] = useState(false);
  const [zoneDragPosition, setZoneDragPosition] = useState<{ x: number; y: number } | null>(null);
  const [zoneDragColor, setZoneDragColor] = useState<ThemeColor | undefined>(undefined);
  const zonePaletteContainerRef = useRef<HTMLDivElement>(null);

  const [descriptionPanelDragPosition, setDescriptionPanelDragPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const isDraggingDescriptionPanelRef = useRef(false);

  const handleDescriptionPanelMouseDown = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (e.button !== 0 || !isSingleViewSelected) return;
      e.preventDefault();

      const startX = e.clientX;
      const startY = e.clientY;
      isDraggingDescriptionPanelRef.current = false;

      const cleanup = () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('blur', handleWindowBlur);
        document.body.style.cursor = '';
        setDescriptionPanelDragPosition(null);
      };

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (moveEvent.buttons === 0) {
          isDraggingDescriptionPanelRef.current = false;
          cleanup();
          return;
        }
        if (!isDraggingDescriptionPanelRef.current) {
          const dx = moveEvent.clientX - startX;
          const dy = moveEvent.clientY - startY;
          if (dx * dx + dy * dy >= DESCRIPTION_PANEL_DRAG_THRESHOLD * DESCRIPTION_PANEL_DRAG_THRESHOLD) {
            isDraggingDescriptionPanelRef.current = true;
            document.body.style.cursor = 'grabbing';
          }
        }
        if (isDraggingDescriptionPanelRef.current) {
          setDescriptionPanelDragPosition({ x: moveEvent.clientX, y: moveEvent.clientY });
        }
      };

      const handleMouseUp = (upEvent: MouseEvent) => {
        const wasDragging = isDraggingDescriptionPanelRef.current;
        isDraggingDescriptionPanelRef.current = false;
        cleanup();

        if (wasDragging) {
          const isOutsideViewport =
            upEvent.clientX < 0 ||
            upEvent.clientX > window.innerWidth ||
            upEvent.clientY < 0 ||
            upEvent.clientY > window.innerHeight;
          if (!isOutsideViewport) {
            handleAddDescriptionPanelByDrag({ x: upEvent.clientX, y: upEvent.clientY });
          }
        } else {
          handleAddDescriptionPanel();
        }
      };

      const handleWindowBlur = () => {
        isDraggingDescriptionPanelRef.current = false;
        cleanup();
      };

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('blur', handleWindowBlur);
    },
    [isSingleViewSelected, handleAddDescriptionPanel, handleAddDescriptionPanelByDrag]
  );

  const handleDescriptionPanelKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (!isSingleViewSelected) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleAddDescriptionPanel();
      }
    },
    [isSingleViewSelected, handleAddDescriptionPanel]
  );

  const handleToggleNodePalette = useCallback(() => {
    // Button は disabled 時もクリックを抑止しないためここでガードする
    if (!isSingleViewSelected) return;
    setIsNodePaletteOpen((prev) => !prev);
    setIsZonePaletteOpen(false);
  }, [isSingleViewSelected]);

  const handleToggleZonePalette = useCallback(() => {
    if (!isSingleViewSelected) return;
    setIsZonePaletteOpen((prev) => !prev);
    setIsNodePaletteOpen(false);
  }, [isSingleViewSelected]);

  // 単一ビュー選択でなくなったらパレットを閉じる
  useEffect(() => {
    if (!isSingleViewSelected) {
      setIsNodePaletteOpen(false);
      setIsZonePaletteOpen(false);
    }
  }, [isSingleViewSelected]);

  // ノードパレット表示中にコンテナ外をクリックしたらパレットを閉じる
  useEffect(() => {
    if (!isNodePaletteOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (nodePaletteContainerRef.current && !nodePaletteContainerRef.current.contains(e.target as Node)) {
        setIsNodePaletteOpen(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isNodePaletteOpen]);

  // ゾーンパレット表示中にコンテナ外をクリックしたらパレットを閉じる
  useEffect(() => {
    if (!isZonePaletteOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (zonePaletteContainerRef.current && !zonePaletteContainerRef.current.contains(e.target as Node)) {
        setIsZonePaletteOpen(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isZonePaletteOpen]);

  const handleClickNodeColor = useCallback(
    (color: ThemeColor) => {
      setIsNodePaletteOpen(false);
      handleAddNodeWithColor(color);
    },
    [handleAddNodeWithColor]
  );

  const handleNodeColorDrag = useCallback((clientPoint: { x: number; y: number }, color: ThemeColor) => {
    setNodeDragPosition(clientPoint);
    setNodeDragColor(color);
  }, []);

  const handleNodeColorDragEnd = useCallback(
    (clientPoint: { x: number; y: number }, color: ThemeColor) => {
      setNodeDragPosition(null);
      setNodeDragColor(undefined);
      setIsNodePaletteOpen(false);
      handleAddNodeByDragWithColor(clientPoint, color);
    },
    [handleAddNodeByDragWithColor]
  );

  const handleNodeColorDragCancel = useCallback(() => {
    setNodeDragPosition(null);
    setNodeDragColor(undefined);
  }, []);

  const handleClickZoneColor = useCallback(
    (color: ThemeColor) => {
      setIsZonePaletteOpen(false);
      handleAddZoneWithColor(color);
    },
    [handleAddZoneWithColor]
  );

  const handleZoneColorDrag = useCallback((clientPoint: { x: number; y: number }, color: ThemeColor) => {
    setZoneDragPosition(clientPoint);
    setZoneDragColor(color);
  }, []);

  const handleZoneColorDragEnd = useCallback(
    (clientPoint: { x: number; y: number }, color: ThemeColor) => {
      setZoneDragPosition(null);
      setZoneDragColor(undefined);
      setIsZonePaletteOpen(false);
      handleAddZoneByDragWithColor(clientPoint, color);
    },
    [handleAddZoneByDragWithColor]
  );

  const handleZoneColorDragCancel = useCallback(() => {
    setZoneDragPosition(null);
    setZoneDragColor(undefined);
  }, []);

  const handleCommentMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      if (!isSingleViewSelected || hasCreatingComment) return;
      e.preventDefault();
      const startX = e.clientX;
      const startY = e.clientY;
      isDraggingCommentRef.current = false;

      const cleanup = () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('blur', handleWindowBlur);
      };

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (moveEvent.buttons === 0) {
          isDraggingCommentRef.current = false;
          setCommentDragPosition(null);
          cleanup();
          return;
        }
        const dx = moveEvent.clientX - startX;
        const dy = moveEvent.clientY - startY;
        if (!isDraggingCommentRef.current && Math.hypot(dx, dy) > COMMENT_DRAG_THRESHOLD) {
          isDraggingCommentRef.current = true;
        }
        if (isDraggingCommentRef.current) {
          setCommentDragPosition({ x: moveEvent.clientX, y: moveEvent.clientY });
        }
      };

      const handleMouseUp = (upEvent: MouseEvent) => {
        const wasDragging = isDraggingCommentRef.current;
        isDraggingCommentRef.current = false;
        cleanup();
        setCommentDragPosition(null);
        if (!hasCreatingCommentRef.current) {
          if (wasDragging) {
            // viewport 外でのドロップは canvasPoint 変換が不安定になるためガードする
            const isOutsideViewport =
              upEvent.clientX < 0 ||
              upEvent.clientX > window.innerWidth ||
              upEvent.clientY < 0 ||
              upEvent.clientY > window.innerHeight;
            if (!isOutsideViewport) {
              handleAddCommentByDrag({ x: upEvent.clientX, y: upEvent.clientY });
            }
          } else {
            handleAddComment();
          }
        }
      };

      const handleWindowBlur = () => {
        isDraggingCommentRef.current = false;
        setCommentDragPosition(null);
        cleanup();
      };

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('blur', handleWindowBlur);
    },
    [handleAddComment, handleAddCommentByDrag, hasCreatingComment, isSingleViewSelected]
  );

  const handleCommentKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isSingleViewSelected) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleAddComment();
      }
    },
    [isSingleViewSelected, handleAddComment]
  );

  const disabledMessage = t('viewModel.toolbar.selectView');

  return (
    <div className="pointer-events-none absolute left-0 top-0 ml-4 flex h-full flex-col justify-center">
      <div className="flex flex-col justify-center">
        <ButtonGroup>
          <div ref={nodePaletteContainerRef} className="relative">
            <Button
              onClick={handleToggleNodePalette}
              tooltip={isSingleViewSelected ? t('viewModel.toolbar.addNode') : disabledMessage}
              icon={faNoteSticky}
              className="rounded-t-lg"
              disabled={!isSingleViewSelected}
            />
            <NodeColorPalette
              isOpen={isNodePaletteOpen && isSingleViewSelected}
              onClickColor={handleClickNodeColor}
              onColorDrag={handleNodeColorDrag}
              onColorDragEnd={handleNodeColorDragEnd}
              onColorDragCancel={handleNodeColorDragCancel}
            />
          </div>
          <div ref={zonePaletteContainerRef} className="relative">
            <Button
              onClick={handleToggleZonePalette}
              tooltip={isSingleViewSelected ? t('viewModel.toolbar.addZone') : disabledMessage}
              icon={faSquare}
              disabled={!isSingleViewSelected}
            />
            <ZoneColorPalette
              isOpen={isZonePaletteOpen && isSingleViewSelected}
              onClickColor={handleClickZoneColor}
              onColorDrag={handleZoneColorDrag}
              onColorDragEnd={handleZoneColorDragEnd}
              onColorDragCancel={handleZoneColorDragCancel}
            />
          </div>
          <DarkTooltip
            tooltip={isSingleViewSelected ? t('viewModel.toolbar.addDescriptionPanel') : disabledMessage}
            placement="right"
          >
            <button
              type="button"
              onMouseDown={handleDescriptionPanelMouseDown}
              onKeyDown={handleDescriptionPanelKeyDown}
              className={classNames(
                'h-10 w-10 cursor-pointer text-2xl font-bold focus:outline-none',
                !isSingleViewSelected ? 'text-gray-500' : 'text-gray-700 hover:bg-gray-200'
              )}
              aria-label={isSingleViewSelected ? t('viewModel.toolbar.addDescriptionPanel') : disabledMessage}
            >
              <FontAwesomeIcon icon={faFileAlt} />
            </button>
          </DarkTooltip>
          <DarkTooltip
            tooltip={
              hasCreatingComment ? null : isSingleViewSelected ? t('viewModel.toolbar.addComment') : disabledMessage
            }
            placement="right"
          >
            <button
              type="button"
              aria-label={t('viewModel.toolbar.addComment')}
              className={classNames(
                'pointer-events-auto h-10 w-10 cursor-pointer rounded-b-lg text-2xl font-bold focus:outline-none',
                isSingleViewSelected && !hasCreatingComment
                  ? 'cursor-grab text-gray-700 hover:bg-gray-200'
                  : 'text-gray-500'
              )}
              onMouseDown={handleCommentMouseDown}
              onKeyDown={handleCommentKeyDown}
              disabled={!isSingleViewSelected || hasCreatingComment}
            >
              <FontAwesomeIcon icon={faComment} />
            </button>
          </DarkTooltip>
        </ButtonGroup>
        <ButtonGroup className="mt-2">
          <Button
            onClick={onAddViewClicked}
            tooltip={t('canvas.view.add.label')}
            icon={faWindowMaximize}
            className="rounded-lg"
          />
        </ButtonGroup>
      </div>
      <NodeDragGhost position={nodeDragPosition} color={nodeDragColor} />
      <ZoneDragGhost position={zoneDragPosition} color={zoneDragColor} />
      <DescriptionPanelDragGhost position={descriptionPanelDragPosition} />
      <CommentDragGhost position={commentDragPosition} />
    </div>
  );
};

export const ViewModelToolbar = memo(ViewModelToolbarFn, propsAreEqual);
